import { Box, Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'modules/Integrations/domain'
import invariant from 'tiny-invariant'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  NetsuiteAuthenticationFields,
  NetsuiteIntegrationConfig,
  NetsuiteSettingsFields
} from 'Integrations/integrationsConfig/netsuite/netsuite.integration.config'
import { AutoLinkCustomersCard } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/AutoLinkCustomersCard'
import NetsuiteAuthenticationCard from './cards/NetsuiteAuthenticationCard'
import NetsuiteSettingsCard from './cards/NetsuiteSettingsCard'

export const NetsuiteConfigurationManagement = ({
  showValidationErrors,
  updateValidationState,
  updateConfiguration,
  integration
}: ConfigurationManagementProps) => {
  invariant(integration.id === 'NetSuite', 'Integration is not NetSuite')

  const netsuiteConfiguration = useMemo(
    () => integration.configuration as NetsuiteIntegrationConfig,
    [integration.configuration]
  )

  const [config, setConfig] = useState<NetsuiteIntegrationConfig>(
    netsuiteConfiguration
  )
  const [wasConnectionTested, setWasConnectionTested] = useState(false)
  const [areSettingsValid, setAreSettingsValid] = useState(false)

  const updateConfig = useCallback(
    (patch: Partial<NetsuiteIntegrationConfig>) => {
      // `isConnected` is the loader's answer, not something the cards edit, so
      // take it fresh each time — a save leaves our copy a reload behind.
      const next = {
        ...config,
        ...patch,
        isConnected: netsuiteConfiguration.isConnected
      }
      setConfig(next)
      updateConfiguration(next)
    },
    [config, updateConfiguration, netsuiteConfiguration.isConnected]
  )

  const handleAuthenticationChange = useCallback(
    (authentication: NetsuiteAuthenticationFields) => {
      setWasConnectionTested(false)
      updateConfig(authentication)
    },
    [updateConfig]
  )

  const handleSettingsChange = useCallback(
    (settings: NetsuiteSettingsFields) => {
      // Only the settings the test payload carries can make a passed test
      // stale; the item ids are not part of minting a token.
      const hasTestedSettingChanged =
        settings.subsidiaryId !== config.subsidiaryId ||
        settings.defaultItemIncomeAccountId !==
          config.defaultItemIncomeAccountId ||
        settings.defaultTaxScheduleId !== config.defaultTaxScheduleId

      if (hasTestedSettingChanged) {
        setWasConnectionTested(false)
      }

      updateConfig(settings)
    },
    [config, updateConfig]
  )

  const handleConnectionSuccess = useCallback(() => {
    setWasConnectionTested(true)
  }, [])

  const handleAutoLinkChange = useCallback(
    (autoLinkCustomers: boolean) => {
      updateConfig({ autoLinkCustomers })
    },
    [updateConfig]
  )

  // A blank private key field means "keep the stored key", so it only counts as
  // a change once something is typed into it.
  const hasAuthenticationChanged =
    config.netsuiteAccountId !== netsuiteConfiguration.netsuiteAccountId ||
    config.consumerKey !== netsuiteConfiguration.consumerKey ||
    config.certificateId !== netsuiteConfiguration.certificateId ||
    Boolean(config.privateKey)

  // Changing a default alone never needs the credentials re-testing, so a
  // connected account saves those straight away.
  const isAuthenticationConfirmed =
    wasConnectionTested ||
    (netsuiteConfiguration.isConnected && !hasAuthenticationChanged)

  const isConfigurationValid = areSettingsValid && isAuthenticationConfirmed

  // `updateValidationState` is rebuilt on every editor update, so it can't go in
  // the dependencies below — the report would re-run on every keystroke, and
  // each report is itself an editor update. Hold the latest one instead.
  const reportValidationState = useRef(updateValidationState)

  useEffect(() => {
    reportValidationState.current = updateValidationState
  })

  useEffect(() => {
    reportValidationState.current(isConfigurationValid)
  }, [isConfigurationValid])

  const initialAuthentication = useMemo(
    () => ({
      netsuiteAccountId: netsuiteConfiguration.netsuiteAccountId,
      consumerKey: netsuiteConfiguration.consumerKey,
      certificateId: netsuiteConfiguration.certificateId,
      privateKey: netsuiteConfiguration.privateKey
    }),
    [netsuiteConfiguration]
  )

  // The backend resolves this across the global, integration and entity
  // levels, so anything other than ALLOW means a level above this toggle is
  // denying the push. It's undefined until a toggle exists.
  const parentLevelBlocksSync =
    Boolean(netsuiteConfiguration.autoLinkSyncPermission) &&
    netsuiteConfiguration.autoLinkSyncPermission !== 'ALLOW'

  const autoLinkPaused = config.autoLinkCustomers && parentLevelBlocksSync

  const initialSettings = useMemo(
    () => ({
      subsidiaryId: netsuiteConfiguration.subsidiaryId,
      defaultItemIncomeAccountId:
        netsuiteConfiguration.defaultItemIncomeAccountId,
      defaultTaxScheduleId: netsuiteConfiguration.defaultTaxScheduleId,
      prepaidCreditItemId: netsuiteConfiguration.prepaidCreditItemId,
      commitmentItemId: netsuiteConfiguration.commitmentItemId,
      creditGrantItemId: netsuiteConfiguration.creditGrantItemId,
      salesTaxItemId: netsuiteConfiguration.salesTaxItemId,
      deferredRevenueItemId: netsuiteConfiguration.deferredRevenueItemId
    }),
    [netsuiteConfiguration]
  )

  return (
    <Box width="100%" data-testid="netsuiteConfiguration">
      <Flex grow={1} width="100%">
        <Flex flexDirection="column" gap="20px" grow={1}>
          {netsuiteConfiguration.isConnected && (
            <AutoLinkCustomersCard
              service="NetSuite"
              value={config.autoLinkCustomers}
              paused={autoLinkPaused}
              onChange={handleAutoLinkChange}
            />
          )}

          <NetsuiteAuthenticationCard
            existingAuthentication={initialAuthentication}
            isConnected={netsuiteConfiguration.isConnected}
            areSettingsValid={areSettingsValid}
            settings={{
              subsidiaryId: config.subsidiaryId,
              defaultItemIncomeAccountId: config.defaultItemIncomeAccountId,
              defaultTaxScheduleId: config.defaultTaxScheduleId
            }}
            showValidationErrors={showValidationErrors}
            onConnectionSuccess={handleConnectionSuccess}
            onAuthenticationChange={handleAuthenticationChange}
          />

          <NetsuiteSettingsCard
            existingSettings={initialSettings}
            showValidationErrors={showValidationErrors}
            onChange={handleSettingsChange}
            onValidationStateChange={setAreSettingsValid}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
